/**
 * PWA Analytics helper.
 *
 * Sends anonymous PWA usage events to the backend AND to PostHog (already
 * loaded globally in index.html). Also fires a Google Ads conversion when
 * a real `appinstalled` event is received.
 *
 * `device_id` is a random UUID generated client-side on first call, stored
 * in localStorage. It is NEVER linkable to a user account — purely used to
 * compute unique-device counts on the admin dashboard.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEVICE_ID_KEY = 'allmx_pwa_device_id';

const uuidv4 = () => {
  // Lightweight UUID v4 (no external dep, no crypto.subtle dependency)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getDeviceId = () => {
  if (typeof window === 'undefined') return null;
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = uuidv4();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
};

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

export const isStandalonePWA = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

export const trackPwaEvent = (event) => {
  if (typeof window === 'undefined') return;
  const payload = {
    event,
    platform: detectPlatform(),
    device_id: getDeviceId(),
  };

  // 1) Backend (own counters)
  try {
    fetch(`${BACKEND_URL}/api/pwa/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true, // survives page unload (e.g. after 'installed')
    }).catch(() => {});
  } catch (_) {
    /* never break the UI */
  }

  // 2) PostHog (rich product analytics)
  try {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(`pwa_${event}`, {
        platform: payload.platform,
        device_id: payload.device_id,
      });
    }
  } catch (_) {
    /* noop */
  }

  // 3) Google Ads conversion only when the browser CONFIRMS install
  if (event === 'installed') {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18098930989',
          event_category: 'pwa',
          event_label: 'app_installed',
        });
      }
    } catch (_) {
      /* noop */
    }
  }
};
